import React, { useMemo, useState } from "react";

import ClearIcon from "@mui/icons-material/Clear";
import { Stack } from "@mui/system";
import Decimal from "decimal.js";

import {
  TableContainerV2,
  TableHeaderV2,
} from "../../Client/Portfolio/shared/shared";
import BasicTableChip from "@src/sharedComponents/BasicTableChip/BasicTableChip";
import { BorderedRow } from "@src/sharedComponents/List/BorderedList";
import Text from "@src/sharedComponents/Typography/Text";

import SubtleSpan from "./SubtleSpan";

import styles from "./Classification.module.css";

export type DrillInDataRow = {
  type: "DataRow";
  row: React.ReactNode;
  weight: Decimal;
};

export type DrillInRow = {
  type: "DrillInRow";
  title: React.ReactNode;
  leftColumn: React.ReactNode;
  rightColumn: React.ReactNode;
  data: DrillInData[];
  weight: Decimal;
};

export type DrillInData = DrillInDataRow | DrillInRow;

interface Props {
  leftTitle: string;
  rightTitle: React.ReactNode;
  leftColumn: string;
  rightColumn: string;
  data: DrillInData[];
  resetView: () => void;
}

const DrillInTable = (props: Props): JSX.Element => {
  const [path, setPath] = useState<number[]>([]);

  const currentData = useMemo(() => {
    return path.reduce((data, pathIndex) => {
      const currentData = data[pathIndex];
      switch (currentData.type) {
        case "DataRow":
          return [];

        case "DrillInRow":
          return currentData.data;
      }
    }, props.data);
  }, [path, props.data]);

  const parent = useMemo(() => {
    if (path.length === 0) {
      return null;
    }

    let parentData = props.data;
    const withOutLastIndex = path.slice(0, path.length - 1);
    const lastIndex = path[path.length - 1];

    for (const pathIndex of withOutLastIndex) {
      const newRow = parentData[pathIndex];
      if (newRow.type === "DataRow") {
        return null;
      }
      parentData = newRow.data;
    }

    const parent = parentData[lastIndex];
    switch (parent.type) {
      case "DataRow":
        return null;

      case "DrillInRow":
        return parent.title;
    }
  }, [path, props.data]);

  return (
    <TableContainerV2>
      <TableHeaderV2
        leftTitle={path.length === 0 ? props.leftTitle : parent}
        rightTitle={props.rightTitle}
        onBack={
          path.length === 0
            ? undefined
            : () => {
                setPath(path.slice(0, path.length - 1));
              }
        }
      />

      <BorderedRow
        left={
          <BasicTableChip
            text={"Show Sleeves"}
            isSelected={true}
            onClick={props.resetView}
          />
        }
        right={
          <div className={styles.clearPill} onClick={props.resetView}>
            <div className={styles.clearPillText}>Clear</div>
            <ClearIcon className={styles.clearPillIcon} />
          </div>
        }
      />

      <div style={{ paddingLeft: 10, paddingRight: 10 }}>
        <BorderedRow
          left={<SubtleSpan>{props.leftColumn}</SubtleSpan>}
          right={<SubtleSpan>{props.rightColumn}</SubtleSpan>}
        />

        {currentData.map((row, index) =>
          row.type === "DrillInRow" ? (
            <BorderedRow
              key={[...path, index].join("/")}
              left={row.leftColumn}
              right={row.rightColumn}
              onClick={() => setPath([...path, index])}
            />
          ) : (
            row.row
          )
        )}
      </div>

      <BorderedRow
        left=""
        removeBorderBottom
        right={
          <Stack direction="row">
            <SubtleSpan style={{ paddingRight: 10 }}>Total </SubtleSpan>
            <Text>
              {currentData
                .reduce(
                  (acc, curr) => Decimal.sum(acc, curr.weight),
                  new Decimal(0)
                )
                .mul(100)
                .toFixed(2)}
              {"%"}
            </Text>
          </Stack>
        }
      />
    </TableContainerV2>
  );
};

export default DrillInTable;
